import { apiClient } from './client'
import type { Request, RequestType, RequestStatus, QuotationReadiness } from './types'

/** Контекст парсинга черновика — сохраняется/восстанавливается с draft'ом. */
export interface IntakeMeta {
  rawText?: string
  parsedLines?: Array<{ line: string; lineNum: number; status: 'ok' | 'warn' | 'skipped' }>
}

/** Предустановленные фильтры KPI-карточек: расшифровываются на бэке в набор условий. */
export type RequestsPresetFilter =
  | 'active'
  | 'burning'
  | 'stale'
  | 'stale14'
  | 'overdue'
  | 'overdue_sales'
  | 'overdue_procurement'
  | 'due_soon'
  | 'due_overdue'

interface RequestsQuery {
  cursor?: string
  limit?: number
  status?: RequestStatus
  /** Набор статусов (CSV) — клик по статусной карточке дашборда помощника. */
  statuses?: string
  /** Сужение по конкретному менеджеру — клик по карточке/ссылке помощника. */
  managerId?: string
  filter?: RequestsPresetFilter
  /** Серверный поиск по номеру заявки, коду/имени клиента, партномеру/бренду позиции. */
  search?: string
  /** Диапазон даты создания (YYYY-MM-DD, включительно) — клик по KPI-карточке
   *  «Аналитика → Сводка»: список = когорта создания периода. */
  createdFrom?: string
  createdTo?: string
  /** Верхняя граница дедлайна (YYYY-MM-DD) — клик по карточке «Горящих дедлайнов». */
  deadlineTo?: string
}

export interface RequestsPage {
  items: Request[]
  hasMore: boolean
  nextCursor: string | null
}

export async function getRequests(query: RequestsQuery = {}): Promise<RequestsPage> {
  const res = await apiClient.get<{ success: true; data: RequestsPage }>('/requests', {
    params: query,
  })
  return res.data.data
}

export interface RequestsSummary {
  active: { count: number; kpSum: number; orderedSum: number }
  processed: { count: number; kpSum: number; orderedSum: number }
}

/** Счётчики и суммы КП по активным/обработанным заявкам — для шапки списка. */
export async function getRequestsSummary(): Promise<RequestsSummary> {
  const res = await apiClient.get<{ success: true; data: RequestsSummary }>('/requests/summary')
  return res.data.data
}

export async function getRequest(id: string): Promise<Request> {
  const res = await apiClient.get<{ success: true; data: Request }>(`/requests/${id}`)
  return res.data.data
}

export async function createRequest(data: {
  clientId: string
  type: RequestType
  deadline?: string | null
  sourceChannel?: string | null
  notes?: string | null
  asDraft?: boolean
  intakeMeta?: IntakeMeta | null
  /** Только для роли ASSISTANT (Помощник): обязательный менеджер-владелец заявки. */
  ownerManagerId?: string
  /** Юрлицо продажи (SellerEntity); не передано — СПЕЦМАШЭЛЕКТРО. */
  sellerEntityId?: string
}): Promise<Request> {
  const res = await apiClient.post<{ success: true; data: Request }>('/requests', data)
  return res.data.data
}

export async function sendToProcurement(id: string): Promise<{ warnings: string[] }> {
  const res = await apiClient.post<{ success: true; data: { warnings: string[] } }>(
    `/requests/${id}/send-to-procurement`,
  )
  return res.data.data
}

export async function markQuotationReady(id: string): Promise<void> {
  await apiClient.post(`/requests/${id}/mark-quotation-ready`)
}

export async function markQuoteSent(id: string): Promise<void> {
  await apiClient.post(`/requests/${id}/mark-quote-sent`)
}

export async function closeRequest(
  id: string,
  outcome: 'WON' | 'LOST',
  rejectionReason?: string | null,
  quotationId?: string | null,
  /** Код причины отказа из списка (19.09.2026). */
  rejectionReasonCode?: string | null,
): Promise<void> {
  // rejectionReason обязателен при outcome=LOST (валидация на бэке — 422).
  // quotationId — для WON: позиции из этого КП переводятся в ORDERED с ценами.
  await apiClient.post(`/requests/${id}/close`, {
    outcome,
    ...(rejectionReason !== undefined ? { rejectionReason } : {}),
    ...(rejectionReasonCode ? { rejectionReasonCode } : {}),
    ...(quotationId ? { quotationId } : {}),
  })
}

export async function getQuotationReadiness(id: string): Promise<QuotationReadiness> {
  const res = await apiClient.get<{ success: true; data: QuotationReadiness }>(
    `/requests/${id}/quotation-readiness`,
  )
  return res.data.data
}

export async function patchRequestStatus(
  id: string,
  status: RequestStatus,
  rejectionReason?: string | null,
  /** Код причины отказа из списка (19.09.2026). */
  rejectionReasonCode?: string | null,
): Promise<void> {
  // Причина обязательна при status=CLOSED_LOST (валидация на бэке).
  await apiClient.patch(`/requests/${id}/status`, {
    status,
    ...(rejectionReason !== undefined ? { rejectionReason } : {}),
    ...(rejectionReasonCode ? { rejectionReasonCode } : {}),
  })
}

export async function patchRequest(
  id: string,
  data: {
    clientId?: string
    type?: string
    deadline?: string | null
    notes?: string | null
    intakeMeta?: IntakeMeta | null
    /** Только для роли ASSISTANT (Помощник): перенос на другого прикреплённого менеджера. */
    ownerManagerId?: string
    /** Проекты (Р12): название и ручной флаг «реализован» (заявки типа PROJECT). */
    projectName?: string | null
    projectRealized?: boolean | null
    /** Юрлицо продажи (SellerEntity): сменить можно, очистить — нет. */
    sellerEntityId?: string
  },
): Promise<Request> {
  const res = await apiClient.patch<{ success: true; data: Request }>(`/requests/${id}`, data)
  return res.data.data
}

/** Проекты (Р12): назначение/снятие ответственного закупщика проекта.
 *  Права на бэке: менеджер/помощник — на свободный проект; РОП/РОЗ/ADMIN и
 *  сам закупщик — переназначение; закупщик может взять проект на себя. */
export async function setResponsibleBuyer(id: string, buyerId: string | null): Promise<Request> {
  const res = await apiClient.patch<{ success: true; data: Request }>(
    `/requests/${id}/responsible-buyer`,
    { responsibleBuyerId: buyerId },
  )
  return res.data.data
}

export async function deleteRequest(id: string): Promise<void> {
  await apiClient.delete(`/requests/${id}`)
}

/** Состав заявки для confirm-диалога удаления («позиций N, квот M, КП K»). */
export interface RequestDeletePreview {
  itemsCount: number
  quotesCount: number
  kpCount: number
  /** Срок хранения в корзине до окончательного удаления, дней. */
  retentionDays: number
}

export async function getRequestDeletePreview(id: string): Promise<RequestDeletePreview> {
  const res = await apiClient.get<{ success: true; data: RequestDeletePreview }>(
    `/requests/${id}/delete-preview`,
  )
  return res.data.data
}

/** Заявка в «Корзине» (мягкое удаление; восстановима 30 дней). */
export interface TrashedRequest {
  id: string
  requestNumber: string | null
  status: string
  createdAt: string
  deletedAt: string
  client: { id: string; name: string; code?: string | null } | null
  manager: { id: string; name?: string | null; email: string } | null
  deletedBy: { id: string; name?: string | null; email: string } | null
  _count: { items: number; quotationSnapshots: number }
}

export async function getTrashedRequests(): Promise<TrashedRequest[]> {
  const res = await apiClient.get<{ success: true; data: TrashedRequest[] }>('/requests/trash')
  return res.data.data
}

/** Восстановление заявки из корзины (ADMIN/MANAGER/ROP, ownership на бэке). */
export async function restoreRequest(id: string): Promise<void> {
  await apiClient.post(`/requests/${id}/restore`)
}

export interface TrashPurgeResult {
  purged: number
  skippedWithOrder: Array<{ id: string; requestNumber: string | null }>
  notFound: string[]
}

/** Окончательное удаление выбранных заявок из корзины (только ADMIN). */
export async function purgeTrashedRequests(ids: string[]): Promise<TrashPurgeResult> {
  const res = await apiClient.post<{ success: true; data: TrashPurgeResult }>(
    '/requests/trash/purge',
    { ids },
  )
  return res.data.data
}

/** Ручная архивация заявки: причина из списка + комментарий (ADMIN/MANAGER/ROP). */
export async function archiveRequest(
  id: string,
  reason: string,
  reasonCode?: string | null,
): Promise<void> {
  await apiClient.post(`/requests/${id}/archive`, {
    reason,
    ...(reasonCode ? { reasonCode } : {}),
  })
}

/** Возврат заявки из архива в статус до архивации (ADMIN/MANAGER/ROP). */
export async function unarchiveRequest(id: string): Promise<void> {
  await apiClient.post(`/requests/${id}/unarchive`)
}

export async function recordProcurementDownload(
  id: string,
  itemIds: string[],
): Promise<{ recorded: number }> {
  const res = await apiClient.post<{ success: true; data: { recorded: number } }>(
    `/requests/${id}/procurement-download`,
    { itemIds },
  )
  return res.data.data
}

export interface ActivityEntry {
  _type: 'status' | 'audit'
  id: string
  // status entries
  fromStatus?: string | null
  toStatus?: string
  note?: string | null
  // audit entries
  entityType?: string
  entityId?: string
  action?: string
  before?: Record<string, unknown> | null
  after?: Record<string, unknown> | null
  // common
  actor: { id: string; email: string; name?: string | null } | null
  createdAt: string
}

export async function getRequestActivity(id: string): Promise<ActivityEntry[]> {
  const res = await apiClient.get<{ success: true; data: ActivityEntry[] }>(
    `/requests/${id}/activity`,
  )
  return res.data.data
}

export async function previewIntake(rawText: string) {
  const res = await apiClient.post<{
    success: true
    data: import('./types').ParseResult
  }>('/request-intake/preview', { rawText })
  return res.data.data
}
